import { LayoutSectionComponent } from '@/components/LayoutSection';
import {
  Button,
  Col,
  FormItem,
  RemoveRedEyeOutlinedIcon,
  Row,
  FormContainer,
} from 'elmo-elements';
import React, { ReactElement } from 'react';
import { CKEditor } from 'ckeditor4-react';
import { QuizFeedbackProps } from '../ResultFeedback';
import config from '@/lib/config';
import { useFormContext } from 'react-hook-form';
import { generatePath, useParams } from 'react-router-dom';
import { ElearningParamType } from '@/pages/Module/routes';
import { useGetQuizQuery } from '@/modules/services';
import { LmsFeedbackMetadata } from '@/modules/types/quiz';
import { getCkEditorConfig } from '@/lib/util';
import { appData } from '@/lib/data';
import PreviewFeedbackResultRoutes from '@/pages/PreviewFeedbackResult/route';

const feedbackInfo = [
  {
    label: 'Pass Feedback',
    name: 'passFeedback',
    ariaLabel: 'Pass feedback',
    id: 'elearning-pass-feedback',
    key: 'elearningPassAgain',
    dataKey: 'lmsContent1',
    previewKey: 'pass',
  },
  {
    label: 'Try Again Feedback',
    name: 'tryFeedback',
    ariaLabel: 'Try again feedback',
    id: 'elearning-try-feedback',
    key: 'elearningTryAgain',
    dataKey: 'lmsRetryContent',
    previewKey: 'retry',
  },
  {
    label: 'Fail Feedback',
    name: 'failFeedback',
    ariaLabel: 'Fail feedback',
    id: 'elearning-fail-feedback',
    key: 'elearningFail',
    dataKey: 'lmsFailContent',
    previewKey: 'fail',
  },
];

const ElearningFeedback = (): ReactElement => {
  const { elearningId } = useParams<ElearningParamType>();
  const quiz = useGetQuizQuery(elearningId);

  const handleFeedbackResultPreview = (feedbackResultType: string) => {
    window.open(
      generatePath(PreviewFeedbackResultRoutes.path, {
        elearningId,
        type: feedbackResultType,
      }),
      'Feedback result preview',
      `width=${appData.playerTheme.default.windowMetadata.width + 5},height=${
        appData.playerTheme.default.windowMetadata.height + 10
      }, location=0,status=0, scrollbars=0, toolbar=0,menubar=0,resizable=1`,
    );
  };

  const {
    setValue,
    formState: { errors },
    getValues,
  } = useFormContext<QuizFeedbackProps>();

  const handleChange = (key: keyof QuizFeedbackProps, value: any) => {
    setValue(key, value);
  };

  return (
    <FormContainer>
      {feedbackInfo.map(
        ({ label, name, ariaLabel, id, key, dataKey, previewKey }) => (
          <FormItem
            label={label}
            message={errors?.[key as keyof QuizFeedbackProps]?.message}
            status={
              errors?.[key as keyof QuizFeedbackProps] ? 'error' : 'default'
            }
            isBlock={true}
            key={key}
          >
            <CKEditor
              editorUrl={config.ckEditorScriptUrl}
              onChange={({ editor }) =>
                handleChange(key as keyof QuizFeedbackProps, editor.getData())
              }
              name={name}
              initData={
                quiz.data &&
                Object.keys(quiz.data.lmsFeedbackMetadata).length > 0
                  ? quiz.data.lmsFeedbackMetadata[
                      dataKey as keyof LmsFeedbackMetadata
                    ]
                  : getValues(key as keyof QuizFeedbackProps)
              }
              aria-label={ariaLabel}
              config={getCkEditorConfig({
                tenantId: `${quiz.data?.tenantId}`,
                type: 'quizzes',
                elearningId,
              })}
            />
            <Row justifyContent="end">
              <Col isAuto={true} grow={0}>
                <Button onClick={() => handleFeedbackResultPreview(previewKey)}>
                  <RemoveRedEyeOutlinedIcon className="margin-sides" />
                  Preview
                </Button>
              </Col>
            </Row>
          </FormItem>
        ),
      )}
    </FormContainer>
  );
};

const ElearningFeedbackComponent = (): ReactElement => (
  <LayoutSectionComponent
    heading="ELMO Learning"
    content={<ElearningFeedback />}
    isFirst={true}
  />
);

export default ElearningFeedbackComponent;
